import type { Paths } from '@shared/types/zod.common.types';
import { z } from 'zod';

const SoftwareInfoSchema = z
  .object({
    androidVersion: z.string().optional().nullable(),
    androidDevicePolicyVersionCode: z.number().optional().nullable(),
    androidDevicePolicyVersionName: z.string().optional().nullable(),
    androidBuildNumber: z.string().optional().nullable(),
    deviceKernelVersion: z.string().optional().nullable(),
    bootloaderVersion: z.string().optional().nullable(),
    androidBuildTime: z.string().optional().nullable(),
    securityPatchLevel: z.string().optional().nullable(),
    primaryLanguageCode: z.string().optional().nullable(),
    deviceBuildSignature: z.string().optional().nullable(),
    systemUpdateInfo: z
      .object({
        updateStatus: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
  })
  .optional()
  .nullable();

const HardwareInfoSchema = z
  .object({
    brand: z.string().optional().nullable(),
    hardware: z.string().optional().nullable(),
    deviceBasebandVersion: z.string().optional().nullable(),
    manufacturer: z.string().optional().nullable(),
    serialNumber: z.string().optional().nullable(),
    model: z.string().optional().nullable(),
    enterpriseSpecificId: z.string().optional().nullable(),
  })
  .optional()
  .nullable();

const TelephonyInfoSchema = z
  .object({
    phoneNumber: z.string().optional().nullable(),
    carrierName: z.string().optional().nullable(),
    iccId: z.string().optional().nullable(),
  })
  .optional()
  .nullable();

const NetworkInfoSchema = z
  .object({
    imei: z.string().optional().nullable(),
    meid: z.string().optional().nullable(),
    wifiMacAddress: z.string().optional().nullable(),
    networkOperatorName: z.string().optional().nullable(),
    telephonyInfos: z.array(TelephonyInfoSchema).optional().nullable(),
  })
  .optional()
  .nullable();

const MemoryEventType = z
  .enum([
    'MEMORY_EVENT_TYPE_UNSPECIFIED',
    'RAM_MEASURED',
    'INTERNAL_STORAGE_MEASURED',
    'EXTERNAL_STORAGE_DETECTED',
    'EXTERNAL_STORAGE_REMOVED',
    'EXTERNAL_STORAGE_MEASURED',
  ])
  .optional()
  .nullable();

const MemoryEventSchema = z
  .object({
    eventType: MemoryEventType,
    createTime: z.string().optional().nullable(),
    byteCount: z.string().optional().nullable(),
  })
  .optional()
  .nullable();

const PowerManagementEventType = z
  .enum([
    'POWER_MANAGEMENT_EVENT_TYPE_UNSPECIFIED',
    'BATTERY_LEVEL_COLLECTED',
    'POWER_CONNECTED',
    'POWER_DISCONNECTED',
    'BATTERY_LOW',
    'BATTERY_OKAY',
    'BOOT_COMPLETED',
    'SHUTDOWN',
  ])
  .optional()
  .nullable();

const PowerManagementEventSchema = z
  .object({
    eventType: PowerManagementEventType,
    createTime: z.string().optional().nullable(),
    batteryLevel: z.number().optional().nullable(),
  })
  .optional()
  .nullable();

const EncryptionStatus = z
  .enum([
    'ENCRYPTION_STATUS_UNSPECIFIED',
    'UNSUPPORTED',
    'INACTIVE',
    'ACTIVATING',
    'ACTIVE',
    'ACTIVE_DEFAULT_KEY',
    'ACTIVE_PER_USER',
  ])
  .optional()
  .nullable();

const DeviceSettingsSchema = z
  .object({
    isDeviceSecure: z.boolean().optional().nullable(),
    adbEnabled: z.boolean().optional().nullable(),
    isEncrypted: z.boolean().optional().nullable(),
    encryptionStatus: z.string().optional().nullable(),
    verifyAppsEnabled: z.boolean().optional().nullable(),
  })
  .optional()
  .nullable();

const DevicePosture = z
  .enum(['POSTURE_UNSPECIFIED', 'SECURE', 'AT_RISK', 'POTENTIALLY_COMPROMISED'])
  .optional()
  .nullable();

const SecurityPostureSchema = z
  .object({
    devicePosture: z.string().optional().nullable(),
    postureDetails: z
      .array(
        z
          .object({
            securityRisk: z.string().optional().nullable(),
          })
          .optional()
          .nullable(),
      )
      .optional()
      .nullable(),
  })
  .optional()
  .nullable();

const SystemPropertiesSchema = z
  .object({
    'ro.product.first_api_level': z.string().optional().nullable(),
  })
  .optional()
  .nullable();

const PasswordRequirementsSchema = z
  .object({
    passwordMinimumLength: z.number().optional().nullable(),
    passwordMinimumLetters: z.number().optional().nullable(),
    passwordMinimumLowerCase: z.number().optional().nullable(),
    passwordMinimumNonLetter: z.number().optional().nullable(),
    passwordMinimumNumeric: z.number().optional().nullable(),
    passwordMinimumSymbols: z.number().optional().nullable(),
    passwordMinimumUpperCase: z.number().optional().nullable(),
  })
  .optional()
  .nullable();

const AndroidDeviceInfoSchema = z
  .object({
    name: z.string().optional().nullable(),
    managementMode: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    appliedState: z.string().optional().nullable(),
    policyCompliant: z.boolean().optional().nullable(),
    enrollmentTime: z.string().optional().nullable(),
    lastStatusReportTime: z.string().optional().nullable(),
    lastPolicySyncTime: z.string().optional().nullable(),
    appliedPolicyVersion: z.string().optional().nullable(),
    appliedPasswordPolicies: z
      .array(PasswordRequirementsSchema)
      .optional()
      .nullable(),
    apiLevel: z.number().optional().nullable(),
    softwareInfo: SoftwareInfoSchema,
    hardwareInfo: HardwareInfoSchema,
    policyName: z.string().optional().nullable(),
    appliedPolicyName: z.string().optional().nullable(),
    networkInfo: NetworkInfoSchema,
    memoryInfo: z
      .object({
        totalRam: z.string().optional().nullable(),
        totalInternalStorage: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
    memoryEvents: z.array(MemoryEventSchema).optional().nullable(),
    powerManagementEvents: z
      .array(PowerManagementEventSchema)
      .optional()
      .nullable(),
    userName: z.string().optional().nullable(),
    enrollmentTokenName: z.string().optional().nullable(),
    deviceSettings: DeviceSettingsSchema,
    systemProperties: SystemPropertiesSchema,
    securityPosture: SecurityPostureSchema,
    ownership: z.string().optional().nullable(),
  })
  .optional()
  .nullable();

export const AndroidDeviceDetailRootSchema = z.object({
  details: z.object({
    id: z.string().uuid(),
    device_id: z.string().uuid(),
    details: AndroidDeviceInfoSchema,
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    android_version: z.string().nullable(),
    api_level: z.number().nullable(),
    serial_number: z.string().nullable(),
    name: z.string(),
    previous_device_names: z.array(z.string()).nullable(),
    state: z.string().nullable(),
    applied_state: z.string().nullable(),
    enrollment_time: z.string().nullable(),
    last_status_report_time: z.string().nullable(),
    policy_name: z.string().nullable(),
    applied_policy_name: z.string().nullable(),
    applied_policy_version: z.number().nullable(),
    policy_compliant: z.boolean().nullable(),
    non_compliance_details: z.array(z.record(z.any())).nullable(),
    last_policy_sync_time: z.string().nullable(),
    last_policy_compliance_report_time: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  }),
});

export type AndroidPasswordRequirements = z.infer<
  typeof PasswordRequirementsSchema
>;
export type AndroidMemoryEvent = z.infer<typeof MemoryEventSchema>;
export type AndroidPowerManagementEvent = z.infer<
  typeof PowerManagementEventSchema
>;
export type AndroidDevicePosture = z.infer<typeof DevicePosture>;
export type AndroidEncryptionStatus = z.infer<typeof EncryptionStatus>;
export type AndroidDeviceDetail = z.infer<typeof AndroidDeviceDetailRootSchema>;
export type AndroidTelephonyInfo = z.infer<typeof TelephonyInfoSchema>;
export type AndroidDetailsDataPaths = Paths<
  typeof AndroidDeviceDetailRootSchema
>;
